import { useEffect } from "react";

import { useRouter } from "@lib/router";
import { ROUTES } from "@utils/constants";

import { DeliveryMethod, OrderFields, OrderStep, SHIPMENT_STEP_DELIVERY_METHODS } from "../../constants";
import useOrderData from "../../context/useOrderData";
import validateOrderFormData from "../../models/orderForm";
import type { TAffiliateData } from "../../types";
import useOrderStorage from "../storage/useOrderStorage";

const getPreviousStep = (currentStep: OrderStep, deliveryMethod?: DeliveryMethod): OrderStep | undefined => {
  if (currentStep === OrderStep.ShipmentDetails || currentStep === OrderStep.SenderDetails) {
    return OrderStep.PackageInfo;
  }
  if (currentStep === OrderStep.ReceiverDetails) {
    return OrderStep.SenderDetails;
  }
  if (currentStep === OrderStep.Summary) {
    const isShipmentStep = deliveryMethod && SHIPMENT_STEP_DELIVERY_METHODS.includes(deliveryMethod);
    return isShipmentStep ? OrderStep.ShipmentDetails : OrderStep.ReceiverDetails;
  }
  return undefined;
};

const getHasCorrectAffiliateData = (step: OrderStep, affiliateData: TAffiliateData | null) => {
  if (!affiliateData || ![OrderStep.ShipmentDetails, OrderStep.SenderDetails].includes(step)) {
    return false;
  }
  return (
    (step === OrderStep.ShipmentDetails &&
      [DeliveryMethod.PudoToPudo, DeliveryMethod.PudoToDoor].includes(
        affiliateData.deliveryMethod as DeliveryMethod
      )) ||
    (step === OrderStep.SenderDetails &&
      [DeliveryMethod.DoorToDoor, DeliveryMethod.DoorToPudo].includes(affiliateData.deliveryMethod as DeliveryMethod))
  );
};

const useRedirectOnInvalidOrder = (step: OrderStep): void => {
  const { getData } = useOrderStorage();

  const {
    staticData: { affiliateData },
  } = useOrderData();

  const router = useRouter();

  useEffect(() => {
    if (step === OrderStep.PackageInfo) {
      return;
    }

    const hasCorrectAffiliateData = getHasCorrectAffiliateData(step, affiliateData);
    if (hasCorrectAffiliateData) {
      return;
    }

    const currentStoredData = getData();
    const deliveryMethod = currentStoredData?.[OrderFields.DELIVERY_METHOD];

    const previousStep = getPreviousStep(step, deliveryMethod as DeliveryMethod);

    const storedData = getData(previousStep);
    if (!storedData) {
      router.replace(ROUTES.ORDER);
      return;
    }

    // Shipment step is available only for specific delivery methods
    if (
      step === OrderStep.ShipmentDetails &&
      !SHIPMENT_STEP_DELIVERY_METHODS.includes(deliveryMethod as DeliveryMethod)
    ) {
      router.replace(ROUTES.ORDER);
      return;
    }

    try {
      validateOrderFormData(storedData);
    } catch (err) {
      console.error("Order data is invalid", err);
      router.replace(ROUTES.ORDER);
    }
  }, [affiliateData, getData, router, step]);
};
export default useRedirectOnInvalidOrder;
